import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api'

type Assignments = Record<string,string>

type ExperimentsContextValue = {
  variants: Assignments
  get: (key: string, fallback?: string) => string
  exposure: (key: string) => void
  refresh: () => Promise<void>
}

const ExperimentsContext = createContext<ExperimentsContextValue | undefined>(undefined)

export const ExperimentsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [variants, setVariants] = useState<Assignments>({})
  const exposed = useRef<Set<string>>(new Set())

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/ui/experiments')
      if (data && typeof data === 'object') setVariants(data as Assignments)
    } catch (error) {
      console.warn('Failed to load experiments', error)
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const exposure = useCallback(async (key: string) => {
    if (exposed.current.has(key)) return
    exposed.current.add(key)
    try {
      await api.post('/ui/experiment/exposure', { experimentKey: key, variant: variants[key] || 'control' })
    } catch (error) {
      console.warn('Failed to record experiment exposure', error)
    }
  }, [variants])

  const get = useCallback((key: string, fallback = 'control') => variants[key] || fallback, [variants])

  const value = useMemo(() => ({ variants, get, exposure, refresh }), [variants, get, exposure, refresh])
  return <ExperimentsContext.Provider value={value}>{children}</ExperimentsContext.Provider>
}

export function useExperiments() {
  const ctx = useContext(ExperimentsContext)
  if (!ctx) throw new Error('useExperiments must be used within ExperimentsProvider')
  return ctx
}
